import cx from 'classnames'
import styles from './Skeleton.module.css'
import { match } from '@/utils/pattern-match-util'
import { Children, createContext, type ReactNode, use, useContext } from 'react'

export {
  SkeletonList,
  type SkeletonListChildren,
  type SkeletonListContextProps,
  useSkeletonListContext,
} from './SkeletonList'

/**
 * The shape and form of the skeleton.
 */
export type SkeletonVariant = 'text' | 'rect' | 'circle'

/**
 * The color of the skeleton. Use the variant that matches the color of the
 * surface it is being used on.
 */
export type SkeletonColor = 'primary' | 'secondary'

export interface SkeletonProps {
  /**
   * The variant of the skeleton. Defaults to 'text'.
   */
  variant?: SkeletonVariant

  /**
   * The color of the skeleton.
   *
   * @default 'primary'
   */
  color?: SkeletonColor

  /**
   * The width of the skeleton. Defaults to 100%.
   */
  width?: number | string

  /**
   * The height of the skeleton.
   *
   * When using `text`, the default height is based on the font size of the
   * containing element.
   */
  height?: number | string

  /**
   * Whether to render as a block. Default is `false`, which renders it
   * as an inline-block
   */
  block?: boolean

  /**
   * Whether to disable positional corrections for text skeletons.
   * Enable this when you want the skeleton to be centered.
   */
  disableCorrections?: boolean

  /**
   * When using the `text` variant, sometimes minor corrections are needed to
   * make the skeleton appear closer to the real content.
   * This will apply some translation to the skeleton.
   * This overrides the default correction.
   */
  topCorrection?: string | number

  /**
   * If specified, the children will be used to measure the actual size but
   * will have opacity applied to make them invisible.
   */
  children?: ReactNode

  /**
   * Customized border radius.
   */
  borderRadius?: number | string
}

/**
 * Context to propagate the color of the skeleton.
 */
export const SkeletonColorContext = createContext<SkeletonColor | undefined>(
  undefined,
)

/**
 * Gets the current skeleton color to use.
 *
 * @param color
 */
export function useSkeletonColor(color?: SkeletonColor): SkeletonColor {
  return color ?? use(SkeletonColorContext) ?? 'primary'
}

/**
 * Renders a skeleton, takes up full-width by default but can be customized.
 */
export function Skeleton({
  variant = 'text',
  borderRadius,
  block,
  width,
  height,
  disableCorrections,
  topCorrection,
  children,
  color,
}: SkeletonProps) {
  color = useSkeletonColor(color)

  const hasChildren = Children.count(children) > 0
  topCorrection = disableCorrections
    ? undefined
    : typeof topCorrection === 'number'
      ? `${topCorrection}px`
      : topCorrection
  return (
    <span
      style={{
        width,
        height,
        maxWidth: width,
        maxHeight: height,
        transform: topCorrection ? `translateY(${topCorrection})` : undefined,
        borderRadius,
      }}
      className={cx(
        styles.root,
        block && styles.block,
        hasChildren && styles.auto,
        disableCorrections && styles.disableCorrections,
        match(color, {
          primary: styles.colorPrimary,
          secondary: styles.colorSecondary,
        }),
        match(variant, {
          text: styles.text,
          rect: styles.rect,
          circle: styles.circle,
        }),
      )}
    >
      {hasChildren && (
        <span aria-hidden className={styles.children}>
          {children}
        </span>
      )}
    </span>
  )
}

interface SkeletonOverlayProps {
  /**
   * Border radius to apply. Defaults to `inherit`.
   */
  borderRadius?: string | number

  /**
   * The color of the skeleton.
   *
   * @default 'primary'
   */
  color?: SkeletonColor
}

/**
 * Renders a skeleton as an absolutely positioned overlay.
 */
export function SkeletonOverlay({ borderRadius, color }: SkeletonOverlayProps) {
  color = useSkeletonColor(color)
  return (
    <span
      style={{ borderRadius }}
      className={cx(
        styles.root,
        styles.overlay,
        match(color, {
          primary: styles.colorPrimary,
          secondary: styles.colorSecondary,
        }),
      )}
    />
  )
}

interface AutoSkeletonProps extends SkeletonProps {
  children?: ReactNode

  /**
   * When `true`, renders as a skeleton, otherwise renders the children.
   */
  isSkeleton?: boolean

  /**
   * If specified and {@link isSkeleton} is `true`, will render approximately
   * this many characters instead of rendering `children`.
   */
  characters?: number
}

/**
 * An inline skeleton wrapper that takes up whatever space is used by
 * the children, and is only rendered if `isSkeleton` is `true`.
 *
 * This component is Skeleton context-aware.
 */
export function AutoSkeleton({
  isSkeleton,
  children,
  characters,
  ...rest
}: AutoSkeletonProps) {
  isSkeleton = useIsSkeleton(isSkeleton)

  let placeholder: ReactNode | null = null
  if (characters && isSkeleton) {
    let str = ''
    for (let i = 0; i < characters; i++) {
      str += i % 2 === 0 ? ' ' : 'a'
    }

    placeholder = <span aria-label="Placeholder">{str}</span>
  }

  return isSkeleton ? (
    <Skeleton {...rest}>{placeholder ?? children ?? 'Placeholder'}</Skeleton>
  ) : (
    children
  )
}

/**
 * Contexts for propagating skeleton state to skeleton-aware components.
 * When either `true` or `false`, will provide the value to any component
 * that doesn't explicitly handle the skeleton state.
 */
export const IsSkeletonContext = createContext<boolean | undefined>(undefined)

/**
 * Checks the {@link IsSkeletonContext} for `true` or `false`.
 * If a local value is provided and is not undefined, the local value wins.
 * If neither a context nor local value is provided, returns `false`.
 *
 * @param localIsSkeleton The local skeleton state. Takes precedence
 *  when specified.
 */
export function useIsSkeleton(localIsSkeleton?: boolean): boolean {
  const contextValue = useContext(IsSkeletonContext)
  return localIsSkeleton ?? contextValue ?? false
}
